/**
 * Recibir un equipo.
 *
 * ES EL TALON DE PAPEL, EN PANTALLA
 * ---------------------------------
 * MANZANA MORDIDA recibe los equipos con un talonario de dos partes: una viaja
 * con el equipo al servicio tecnico tercerizado, la otra queda en el local. Los
 * campos de este formulario son los del talon, en cuatro pasos que siguen el
 * orden del papel: el equipo, de quien es, como se abre, adonde va. La fecha y
 * el numero los pone la base. Ver `PASOS_RECEPCION` en `logica.ts`.
 *
 * Y NADA MAS QUE EL TALON
 * -----------------------
 * Dos campos obligatorios: que equipo es y que le pasa. Todo lo demas, el
 * cliente incluido, puede esperar. Entra alguien apurado, deja el equipo y se
 * va; una orden con dos campos existe y se completa despues, una orden que
 * nunca se cargo no se completa nunca.
 *
 * Ademas del talon se pide el IMEI: es lo que distingue dos equipos iguales y
 * lo que permite encontrar la orden cuando el equipo vuelve. Lo que NO se
 * pregunta, a proposito: modelo, color y accesorios van en texto libre como en
 * el papel; la plata se define cuando el tecnico contesta, no al recibir; y la
 * garantia es siempre la misma, asi que se aplica sola. Una sola pantalla, sin
 * pliegues.
 *
 * El patron de desbloqueo se dibuja en la misma grilla de nueve puntos que
 * esta impresa en el papel. Ver `PatronDesbloqueo`.
 *
 * Y POR QUE NO TIENE PASO DE REVISION
 * -----------------------------------
 * Recibir un equipo no escribe un asiento ni mueve stock: crea una orden en
 * RECIBIDO, que es el estado del que se puede salir para cualquier lado. Lo
 * caro aca no es equivocarse, es no cargar.
 *
 * LO CARGADO NO SE PIERDE DE UN TOQUE
 * -----------------------------------
 * Se carga con el cliente adelante y el equipo en la mano. Antes, un toque
 * fuera del cuadro lo cerraba y Cancelar borraba todo sin preguntar; y un
 * cliente nuevo con nombre y teléfono escritos se perdía si se tocaba
 * Siguiente sin «Cargar a …». Ahora tocar afuera no hace nada, cerrar con algo
 * escrito pregunta, y Siguiente carga al cliente que quedó a medias (decidido
 * por el usuario el 05/11).
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, User, UserPlus, X } from "lucide-react";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import { Pasos } from "@/components/ui/pasos";
import { Segmented, type OpcionSegmented } from "@/components/ui/segmented";
import { SelectNativo } from "@/components/ui/select-nativo";
import { Textarea } from "@/components/ui/textarea";
import { Importe } from "@/components/money/importe";
import { InputImporte } from "@/components/money/input-importe";
import { convertir, type Moneda } from "@tora/shared";
import type {
  Cliente,
  NuevaOrdenReparacion,
  NuevoCliente,
  Producto,
  SugerenciasOt,
  UsadoEnTaller,
} from "@/lib/tipos";

import { ElegirUsado } from "./ElegirUsado";

/** Una caja donde puede entrar el cobro del diagnóstico. */
export interface CajaDelCobro {
  id: number;
  nombre: string;
  moneda: Moneda;
}

import {
  armarRecepcion,
  formularioVacio,
  normalizar,
  pasosDeRecepcion,
  precargarUsado,
  problemasDeLosPasos,
  puedeAvanzar,
  validarRecepcion,
  type FormularioRecepcion,
  type PasoRecepcion,
} from "./logica";
import { PatronDesbloqueo } from "./PatronDesbloqueo";

/** Clave o patron: como se abre el equipo. Es una cosa o la otra, como en el papel. */
const OPCIONES_CLAVE: ReadonlyArray<OpcionSegmented<FormularioRecepcion["tipoClave"]>> = [
  { valor: "pin", etiqueta: "Número" },
  { valor: "patron", etiqueta: "Patrón" },
];

export interface DialogRecibirProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  clientes: ReadonlyArray<Cliente>;
  /** Las cajas donde puede entrar el cobro del diagnóstico. */
  cuentas?: ReadonlyArray<CajaDelCobro>;
  cotizacion?: number;
  /** Lo que cobra el local por el diagnóstico, de la configuración. */
  diagnostico?: number;
  guardando?: boolean;
  onConfirmar: (datos: NuevaOrdenReparacion) => void;
  /**
   * Dar de alta al titular con nombre y telefono, sin salir del formulario.
   * Si no viene, el buscador solo elige entre los clientes que ya existen.
   */
  onCrearCliente?: (datos: NuevoCliente) => Promise<Cliente>;
  /**
   * Equipos, transportes y tercerizados ya cargados: se sugieren al tipear y
   * los dos ultimos vienen precargados. Se arma solo con el uso.
   */
  sugerencias?: SugerenciasOt;
  /** Los usados del catálogo: la casilla «Es un equipo nuestro» elige uno de acá. */
  usados?: ReadonlyArray<Producto>;
  /** Cuáles ya están en el taller: se muestran pero no se pueden volver a mandar. */
  enTaller?: ReadonlyMap<number, UsadoEnTaller>;
  /** Abrir ya con este usado elegido (el atajo desde la permuta). */
  productoInicial?: number;
}

/** El cliente nuevo que se está escribiendo y todavía no se cargó. */
interface TitularPendiente {
  nombre: string;
  telefono: string;
  /** Si ya se abrió el formulario de alta (tocó «Cargar «…» como cliente nuevo»). */
  enAlta: boolean;
}

/**
 * Los datos del alta desde el taller. La cuenta va en pesos, dicho y no por
 * defecto de la base: una reparación se cobra en pesos, y el anclaje no se
 * cambia después del primer movimiento. Por eso también se avisa en pantalla.
 */
function datosTitularNuevo(nombre: string, telefono: string): NuevoCliente {
  return { nombre, telefono, cc_modo: "FIJA", cc_moneda: "ARS" };
}

export function DialogRecibir({
  abierto,
  onAbiertoChange,
  clientes,
  cuentas = [],
  cotizacion = 0,
  diagnostico = 0,
  guardando = false,
  onConfirmar,
  onCrearCliente,
  sugerencias,
  usados = [],
  enTaller = new Map<number, UsadoEnTaller>(),
  productoInicial,
}: DialogRecibirProps): JSX.Element {
  const [form, setForm] = useState<FormularioRecepcion>(formularioVacio);
  const [paso, setPaso] = useState(0);
  // Los pasos en los que ya se intento seguir: solo esos muestran errores. Un
  // paso que todavia no se vio no puede estar "mal".
  const [intentados, setIntentados] = useState<ReadonlySet<PasoRecepcion>>(new Set());
  // El importe del diagnostico es opcional: vacio = no se cobra. Pero "abc" no es
  // vacio, y `InputImporte` emite null en los dos casos. Con esto el wizard no
  // avanza mientras el texto no se entienda, en vez de recibir "sin cobro".
  const [importeInvalido, setImporteInvalido] = useState(false);
  // Cómo arrancó el formulario al abrir: si cambió, cerrar pregunta antes.
  const [inicial, setInicial] = useState("");
  const [preguntarDescartar, setPreguntarDescartar] = useState(false);
  const [pendiente, setPendiente] = useState<TitularPendiente | null>(null);
  const [avisoTitular, setAvisoTitular] = useState<string | null>(null);
  const [cargandoTitular, setCargandoTitular] = useState(false);

  // Al abrir se arranca limpio y desde el primer paso: un formulario que
  // recuerda el equipo anterior termina cargando dos veces el mismo iPhone.
  useEffect(() => {
    if (!abierto) return;
    const base = formularioVacio({ ...sugerencias?.ultimo, diagnostico });
    const usadoInicial =
      productoInicial === undefined ? null : (usados.find((p) => p.id === productoInicial) ?? null);
    const arranque = usadoInicial
      ? precargarUsado({ ...base, esInterna: true }, usadoInicial)
      : base;
    setForm(arranque);
    setInicial(JSON.stringify(arranque));
    setPaso(0);
    setIntentados(new Set());
    setPreguntarDescartar(false);
    setPendiente(null);
    setAvisoTitular(null);
    // Solo al abrir: las sugerencias no reinician lo que se esta escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  function cambiar<K extends keyof FormularioRecepcion>(
    campo: K,
    valor: FormularioRecepcion[K],
  ): void {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  const pasos = pasosDeRecepcion(form);
  // pasosDeRecepcion() nunca devuelve vacio: siempre queda al menos "equipo".
  const actual = pasos[paso] ?? pasos[0]!;
  const ultimo = paso === pasos.length - 1;
  const validacion = validarRecepcion(form);
  const problemas = problemasDeLosPasos(validacion.problemas, intentados);

  const hayAlgoCargado = JSON.stringify(form) !== inicial || pendiente !== null;

  /** Cancelar, la ✕ o Escape: si hay algo escrito, primero se pregunta. */
  function pedirCerrar(): void {
    if (hayAlgoCargado) setPreguntarDescartar(true);
    else onAbiertoChange(false);
  }

  /**
   * Antes de dejar el paso del titular: si quedó un cliente a medio escribir,
   * se lo carga (con nombre y teléfono) o se avisa qué falta. Devuelve el id
   * del titular que vale, o `false` si no se puede seguir.
   */
  async function resolverTitular(): Promise<number | null | false> {
    if (actual.clave !== "titular" || form.clienteId !== null || !pendiente) {
      return form.clienteId;
    }
    const nombre = pendiente.nombre.trim();
    const telefono = pendiente.telefono.trim();
    if (pendiente.enAlta && telefono && onCrearCliente) {
      setCargandoTitular(true);
      setAvisoTitular(null);
      try {
        const nuevo = await onCrearCliente(datosTitularNuevo(nombre, telefono));
        cambiar("clienteId", nuevo.id);
        setPendiente(null);
        return nuevo.id;
      } catch (err) {
        setAvisoTitular(err instanceof Error ? err.message : "No se pudo cargar el cliente.");
        return false;
      } finally {
        setCargandoTitular(false);
      }
    }
    setAvisoTitular(
      pendiente.enAlta
        ? `Falta el número de contacto para cargar a ${nombre}.`
        : `No elegiste a nadie. Elegí uno de la lista, cargá a «${nombre}» como cliente nuevo o borrá lo que escribiste para seguir sin titular.`,
    );
    return false;
  }

  async function siguiente(): Promise<void> {
    setIntentados((prev) => new Set(prev).add(actual.clave));
    if (importeInvalido || !puedeAvanzar(form, actual.clave)) return;
    if ((await resolverTitular()) === false) return;
    setPaso((p) => Math.min(p + 1, pasos.length - 1));
  }

  function atras(): void {
    setPaso((p) => Math.max(p - 1, 0));
  }

  async function confirmar(): Promise<void> {
    setIntentados(new Set(pasos.map((p) => p.clave)));
    if (importeInvalido) return;
    if (!validacion.ok) {
      // Se vuelve al primer paso que tenga un problema, para que el error se
      // vea y no quede escondido en una pantalla anterior.
      const i = pasos.findIndex((p) => !puedeAvanzar(form, p.clave));
      if (i >= 0) setPaso(i);
      return;
    }
    const titular = await resolverTitular();
    if (titular === false) return;
    onConfirmar(armarRecepcion({ ...form, clienteId: titular }));
  }

  function alternarInterna(marcada: boolean): void {
    setForm((prev) =>
      marcada ? { ...prev, esInterna: true } : { ...prev, esInterna: false, productoId: null },
    );
    setPaso(0);
  }

  /**
   * Enter en un input avanza de paso (MASTER seccion 7): el que carga con el
   * teclado no baja la mano al mouse entre paso y paso. Pasa por `siguiente()`,
   * asi que si el paso no valida se quedan los errores a la vista. En un
   * textarea inserta el salto de linea, en un boton lo activa, y en el ultimo
   * paso no hace nada: el verbo final se aprieta a proposito.
   */
  function alTeclado(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== "Enter" || e.defaultPrevented) return;
    if (!(e.target instanceof HTMLInputElement) || e.target.type === "checkbox") return;
    if (ultimo) return;
    e.preventDefault();
    void siguiente();
  }

  return (
    <Dialog open={abierto} onOpenChange={(a) => (a ? onAbiertoChange(true) : pedirCerrar())}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Recibir un equipo</DialogTitle>
          {/* `Pasos` ya escribe "Paso N de M · titulo" arriba de la barra; con
              `asChild` ese bloque es la descripcion del dialogo para Radix
              (aria-describedby) sin repetir el texto. */}
          <DialogDescription asChild>
            <div>
              <Pasos pasos={pasos.map((p) => p.titulo)} actual={paso} />
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogBody onKeyDown={alTeclado}>
          {actual.clave === "equipo" ? (
            <>
              <Checkbox
                label="Es un equipo nuestro"
                ayuda="Un usado del stock que se manda a reacondicionar. No hay cliente ni se cobra nada: lo que salga se suma al costo del equipo."
                checked={form.esInterna}
                onChange={(e) => alternarInterna(e.target.checked)}
              />

              {form.esInterna ? (
                <ElegirUsado
                  usados={usados}
                  enTaller={enTaller}
                  productoId={form.productoId}
                  onElegir={(p) => setForm((prev) => precargarUsado(prev, p))}
                  problema={problemas.producto}
                />
              ) : null}

              <Campo label="Equipo" requerido error={problemas.equipo}>
                <Input
                  id="ot-equipo"
                  value={form.equipo}
                  onChange={(e) => cambiar("equipo", e.target.value)}
                  placeholder="iPhone 13 Pro 128 GB grafito"
                  autoComplete="off"
                  list="ot-equipos-sugeridos"
                  autoFocus={!form.esInterna}
                />
              </Campo>

              <Campo
                label="IMEI o número de serie"
                ayuda="Una PC o una consola no tienen IMEI: va el número de serie."
              >
                <Input
                  id="ot-imei"
                  value={form.imei}
                  onChange={(e) => cambiar("imei", e.target.value)}
                  placeholder="35 410100 000411 2"
                  autoComplete="off"
                  className="tabular-nums"
                />
              </Campo>

              <Campo
                label="Qué le pasa"
                requerido
                error={problemas.falla}
                ayuda={
                  problemas.falla
                    ? undefined
                    : form.esInterna
                      ? "Qué hay que hacerle. El diagnóstico del técnico se carga después, aparte."
                      : "Con las palabras del cliente. El diagnóstico del técnico se carga después, aparte."
                }
              >
                <Textarea
                  id="ot-falla"
                  value={form.falla}
                  onChange={(e) => cambiar("falla", e.target.value)}
                  placeholder={
                    form.esInterna
                      ? "Batería al 78%, cambiar"
                      : "No enciende, se le rompió la pantalla, no carga…"
                  }
                />
              </Campo>
            </>
          ) : null}

          {actual.clave === "titular" ? (
            <ElegirCliente
              clientes={clientes}
              clienteId={form.clienteId}
              onElegir={(id) => {
                cambiar("clienteId", id);
                setAvisoTitular(null);
              }}
              onCrear={onCrearCliente}
              onPendiente={(p) => {
                setPendiente(p);
                setAvisoTitular(null);
              }}
              aviso={avisoTitular}
            />
          ) : null}

          {actual.clave === "accesos" ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo label="Cuenta">
                  <Input
                    id="ot-cuenta"
                    value={form.cuenta}
                    onChange={(e) => cambiar("cuenta", e.target.value)}
                    placeholder="Apple ID o cuenta de Google"
                    autoComplete="off"
                    inputMode="email"
                    autoFocus
                  />
                </Campo>
                <Campo label="Contraseña">
                  <Input
                    id="ot-password"
                    value={form.password}
                    onChange={(e) => cambiar("password", e.target.value)}
                    placeholder="Clave del equipo o de la cuenta"
                    autoComplete="off"
                  />
                </Campo>
              </div>

              {/* Clave o patron: como se abre el equipo. Es una cosa o la
                  otra, como en el papel, asi que se elige y se carga una. */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Clave o patrón</Label>
                  <Segmented
                    etiqueta="Cómo se abre el equipo"
                    opciones={OPCIONES_CLAVE}
                    valor={form.tipoClave}
                    onChange={(v) => cambiar("tipoClave", v)}
                  />
                </div>

                {form.tipoClave === "pin" ? (
                  <Input
                    id="ot-pin"
                    aria-label="Clave numérica del equipo"
                    value={form.pin}
                    onChange={(e) => cambiar("pin", e.target.value)}
                    placeholder="El número para desbloquearlo"
                    inputMode="numeric"
                    autoComplete="off"
                    className="tabular-nums"
                  />
                ) : (
                  <>
                    <PatronDesbloqueo
                      valor={form.patron}
                      onCambiar={(p) => cambiar("patron", p)}
                      idAyuda={problemas.patron ? "ot-patron-error" : undefined}
                    />
                    {problemas.patron ? (
                      <p id="ot-patron-error" role="alert" className="text-xs text-destructive">
                        {problemas.patron}
                      </p>
                    ) : null}
                  </>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Sin los accesos el técnico no puede probar que el arreglo funcionó. Se borran solos
                al entregar el equipo: no quedan guardados.
              </p>
            </>
          ) : null}

          {actual.clave === "envio" ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo label="Transporte">
                  <Input
                    id="ot-transporte"
                    value={form.transporte}
                    onChange={(e) => cambiar("transporte", e.target.value)}
                    placeholder="Qué comisionista o transporte lo lleva"
                    autoComplete="off"
                    list="ot-transportes-sugeridos"
                    autoFocus
                  />
                </Campo>
                <Campo label="Tercerizado">
                  <Input
                    id="ot-tercerizado"
                    value={form.tercerizado}
                    onChange={(e) => cambiar("tercerizado", e.target.value)}
                    placeholder="A qué servicio técnico se manda"
                    autoComplete="off"
                    list="ot-tercerizados-sugeridos"
                  />
                </Campo>
              </div>

              <Campo label="Observación">
                <Textarea
                  id="ot-obs"
                  value={form.observaciones}
                  onChange={(e) => cambiar("observaciones", e.target.value)}
                  placeholder="Deja cargador y funda. Pantalla rayada de antes, viene sin chip…"
                />
              </Campo>
            </>
          ) : null}

          {/* ── El diagnóstico ──
              El cobro va prendido por defecto: es la regla del negocio. Pero no
              bloquea el alta, porque un equipo en el taller sin orden es peor
              que un equipo sin cobrar. Apagarlo pide el motivo. */}
          {actual.clave === "diagnostico" ? (
            <>
              <Checkbox
                label="Se cobra ahora"
                ayuda="Es lo que habilita la mesa de trabajo. Se descuenta del total al entregar: no es un cargo aparte."
                checked={form.cobraDiagnostico}
                onChange={(e) => cambiar("cobraDiagnostico", e.target.checked)}
              />

              {/* Uno abajo del otro y no en dos columnas: con el selector de
                  moneda al lado, el campo del importe quedaba con lugar para
                  tres dígitos y el número no se leía. */}
              <div className="space-y-4">
                <InputImporte
                  label="Diagnóstico"
                  valor={form.diagnosticoImporte}
                  onValorChange={(v) => cambiar("diagnosticoImporte", v)}
                  onInvalidoChange={setImporteInvalido}
                  moneda={form.diagnosticoMoneda}
                  onMonedaChange={(m) => cambiar("diagnosticoMoneda", m)}
                  cotizacion={cotizacion}
                />
                {form.cobraDiagnostico && cuentas.length > 0 ? (
                  <Campo label="¿En qué caja entra?" error={problemas.diagnostico}>
                    <SelectNativo
                      id="ot-diag-caja"
                      value={form.diagnosticoCuentaId ?? ""}
                      onChange={(e) => {
                        const id = e.target.value === "" ? null : Number(e.target.value);
                        cambiar("diagnosticoCuentaId", id);
                        const cuenta = cuentas.find((cta) => cta.id === id);
                        if (cuenta) {
                          cambiar(
                            "diagnosticoMedio",
                            cuenta.moneda === "USD" ? "EFECTIVO_USD" : "EFECTIVO_ARS",
                          );
                        }
                      }}
                    >
                      <option value="">Elegí una caja</option>
                      {cuentas.map((cta) => (
                        <option key={cta.id} value={cta.id}>
                          {cta.nombre} ({cta.moneda})
                        </option>
                      ))}
                    </SelectNativo>
                  </Campo>
                ) : null}
              </div>

              {form.cobraDiagnostico ? (
                <p className="text-xs text-muted-foreground">
                  <TextoDelCobro form={form} cuentas={cuentas} cotizacion={cotizacion} />
                </p>
              ) : (
                <Campo
                  label="¿Por qué no se cobra?"
                  requerido
                  error={problemas.diagnostico}
                  ayuda="El equipo entra igual y queda anotado que se le debe."
                >
                  <Input
                    id="ot-diag-motivo"
                    value={form.diagnosticoMotivo}
                    onChange={(e) => cambiar("diagnosticoMotivo", e.target.value)}
                    placeholder="Cliente conocido, paga el viernes"
                    autoComplete="off"
                  />
                </Campo>
              )}

              {/* Sin cajas cargadas el error de "en que caja entra" no tiene
                  campo debajo del cual ir: queda suelto, con su role. */}
              {form.cobraDiagnostico && cuentas.length === 0 && problemas.diagnostico ? (
                <p role="alert" className="text-xs text-destructive">
                  {problemas.diagnostico}
                </p>
              ) : null}
            </>
          ) : null}
        </DialogBody>

        {/* Las listas de sugerencias del navegador: aparecen al tipear, no
            ocupan lugar, y no obligan a elegir. */}
        <datalist id="ot-equipos-sugeridos">
          {(sugerencias?.equipos ?? []).map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
        <datalist id="ot-transportes-sugeridos">
          {(sugerencias?.transportes ?? []).map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
        <datalist id="ot-tercerizados-sugeridos">
          {(sugerencias?.tercerizados ?? []).map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>

        <DialogFooter variant="wizard">
          <Button type="button" variant="ghost" onClick={pedirCerrar}>
            Cancelar
          </Button>

          {/* En mobile se apilan con la primaria arriba, igual que el footer. */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            {paso > 0 ? (
              <Button type="button" variant="outline" onClick={atras}>
                <ChevronLeft aria-hidden="true" />
                Atrás
              </Button>
            ) : null}
            {ultimo ? (
              <Button
                type="button"
                cargando={guardando || cargandoTitular}
                onClick={() => void confirmar()}
              >
                Recibir el equipo
              </Button>
            ) : (
              <>
                {/* Con el equipo y la falla alcanza: lo que falta se completa
                    despues. Este atajo es el que se usa en la hora pico. */}
                {/* Desde el paso 2, en el celular no entra en la barra de arriba
                    junto a Cancelar, Atrás y Siguiente (quedaba en dos renglones
                    y Siguiente cambiaba de lugar entre pasos). */}
                <Button
                  type="button"
                  variant="outline"
                  cargando={guardando || cargandoTitular}
                  onClick={() => void confirmar()}
                  className={cn(paso > 0 && "max-sm:hidden")}
                >
                  Recibir ya
                </Button>
                <Button type="button" cargando={cargandoTitular} onClick={() => void siguiente()}>
                  Siguiente
                  <ChevronRight aria-hidden="true" />
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={preguntarDescartar} onOpenChange={setPreguntarDescartar}>
        <DialogContent tamano="sm">
          <DialogHeader>
            <DialogTitle>¿Descartar lo cargado?</DialogTitle>
            <DialogDescription>
              Se pierde lo que escribiste de este equipo. La orden no se crea.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPreguntarDescartar(false)}>
              Seguir cargando
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setPreguntarDescartar(false);
                onAbiertoChange(false);
              }}
            >
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/**
 * El titular y su numero de contacto.
 *
 * El talon los pide como dos renglones; aca son el cliente. Se busca por
 * nombre, telefono o documento, que es lo que hay a mano cuando la persona
 * esta enfrente. Si no aparece, se lo carga ahi mismo con nombre y telefono,
 * que es exactamente lo que dice el papel: no hace falta ir a Clientes ni
 * pedir DNI para recibir un equipo.
 *
 * El estado por defecto es SIN cliente y eso no se marca como un error ni con
 * un asterisco: es una opcion valida y la mas comun en la hora pico.
 */
function ElegirCliente({
  clientes,
  clienteId,
  onElegir,
  onCrear,
  onPendiente,
  aviso,
}: {
  clientes: ReadonlyArray<Cliente>;
  clienteId: number | null;
  onElegir: (id: number | null) => void;
  onCrear?: (datos: NuevoCliente) => Promise<Cliente>;
  /** Lo que quedó escrito sin elegir ni cargar, para que Siguiente no lo pierda. */
  onPendiente?: (pendiente: TitularPendiente | null) => void;
  /** Por qué no se pudo seguir con lo que quedó a medias. */
  aviso?: string | null;
}): JSX.Element {
  const [texto, setTexto] = useState("");
  const [cargandoNuevo, setCargandoNuevo] = useState(false);
  const [telefono, setTelefono] = useState("");
  const [creando, setCreando] = useState(false);
  const [errorAlta, setErrorAlta] = useState<string | null>(null);

  const elegido = useMemo(
    () => clientes.find((c) => c.id === clienteId) ?? null,
    [clientes, clienteId],
  );

  // Lo escrito sin elegir ni cargar sube al wizard, que decide al tocar
  // Siguiente. Con un titular ya elegido no hay nada pendiente.
  const hayPendiente = clienteId === null && texto.trim().length > 0;
  useEffect(() => {
    onPendiente?.(hayPendiente ? { nombre: texto, telefono, enAlta: cargandoNuevo } : null);
    // `onPendiente` es una flecha nueva en cada render del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hayPendiente, texto, telefono, cargandoNuevo]);
  useEffect(() => () => onPendiente?.(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  const sugerencias = useMemo(() => {
    const q = normalizar(texto);
    if (!q) return [];
    return clientes
      .filter((c) =>
        [c.nombre, c.telefono, c.documento].some((v) => (v ? normalizar(v).includes(q) : false)),
      )
      .slice(0, 5);
  }, [clientes, texto]);

  function limpiar(): void {
    setTexto("");
    setTelefono("");
    setCargandoNuevo(false);
    setErrorAlta(null);
  }

  async function crear(): Promise<void> {
    if (!onCrear) return;
    const nombre = texto.trim();
    if (!nombre || !telefono.trim()) return;
    setCreando(true);
    setErrorAlta(null);
    try {
      const nuevo = await onCrear(datosTitularNuevo(nombre, telefono.trim()));
      onElegir(nuevo.id);
      limpiar();
    } catch (err) {
      setErrorAlta(err instanceof Error ? err.message : "No se pudo cargar el cliente.");
    } finally {
      setCreando(false);
    }
  }

  if (elegido) {
    return (
      <div className="space-y-1.5">
        <Label>Titular</Label>
        <Card className="flex items-center justify-between gap-2 py-1 pl-3 pr-1">
          <span className="flex min-w-0 items-center gap-2 text-sm">
            <User className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{elegido.nombre}</span>
            {elegido.telefono ? (
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {elegido.telefono}
              </span>
            ) : null}
          </span>
          <IconButton
            icono={X}
            aria-label="Quitar el cliente"
            tamano="sm"
            onClick={() => {
              onElegir(null);
              limpiar();
            }}
          />
        </Card>
      </div>
    );
  }

  const puedeCrear = onCrear !== undefined && texto.trim().length > 0;

  return (
    <div className="space-y-2">
      <Campo label="Titular (opcional)">
        <InputBusqueda
          id="ot-cliente"
          etiqueta="Titular (opcional)"
          valor={texto}
          onValorChange={(v) => {
            setTexto(v);
            setErrorAlta(null);
          }}
          placeholder="Buscar por nombre, teléfono o documento"
          // Con algo tipeado, Enter no avanza de paso: el que esta buscando
          // quiere elegir de la lista, no perder el titular a medio escribir.
          onKeyDown={(e) => {
            if (e.key === "Enter" && texto.trim()) e.stopPropagation();
          }}
        />
      </Campo>

      {sugerencias.length > 0 ? (
        <Card>
          <ul className="flex flex-col gap-1 p-1">
            {sugerencias.map((c) => (
              <li key={c.id}>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-between font-normal"
                  onClick={() => {
                    onElegir(c.id);
                    limpiar();
                  }}
                >
                  <span className="min-w-0 truncate">{c.nombre}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {c.telefono ?? ""}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {puedeCrear && !cargandoNuevo ? (
        <Button
          type="button"
          variant="link"
          className="justify-start whitespace-normal text-left"
          onClick={() => setCargandoNuevo(true)}
        >
          <UserPlus aria-hidden="true" />
          {sugerencias.length > 0
            ? `No es ninguno: cargar «${texto.trim()}» como cliente nuevo`
            : `Cargar «${texto.trim()}» como cliente nuevo`}
        </Button>
      ) : null}

      {puedeCrear && cargandoNuevo ? (
        <Card variant="muted" padding="normal" className="space-y-4">
          <Campo label="Nº de contacto" requerido>
            <Input
              id="ot-cliente-telefono"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Para avisarle cuando esté listo"
              inputMode="tel"
              autoComplete="off"
              className="tabular-nums"
              // Enter aca carga al cliente, no avanza el wizard.
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                e.stopPropagation();
                void crear();
              }}
            />
          </Campo>
          <p className="text-xs text-muted-foreground">
            Su cuenta queda en pesos. Si va a comprar en dólares a cuenta, cambialo en Clientes
            antes de su primer movimiento.
          </p>
          {errorAlta ? (
            <Aviso tono="destructive" compacto>
              {errorAlta}
            </Aviso>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCargandoNuevo(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              cargando={creando}
              disabled={!telefono.trim()}
              onClick={() => void crear()}
            >
              Cargar a {texto.trim()}
            </Button>
          </div>
        </Card>
      ) : null}

      {aviso ? (
        <Aviso tono="warning" compacto>
          {aviso}
        </Aviso>
      ) : null}

      {sugerencias.length === 0 && !texto.trim() ? (
        <p className="text-xs text-muted-foreground">
          Se puede dejar vacío. La orden vale igual y el cliente se asocia cuando vuelva con los
          datos.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Lo que va a entrar a la caja, dicho en la moneda de esa caja.
 *
 * El diagnóstico se piensa en dólares y la caja del local casi siempre está en
 * pesos. Verlo convertido ANTES de confirmar es lo que evita el cobro por veinte
 * pesos en vez de veinte dólares. El importe se pinta con `Importe`, con su
 * chip y su color: es plata, no texto.
 */
function TextoDelCobro({
  form,
  cuentas,
  cotizacion,
}: {
  form: {
    diagnosticoImporte: number | null;
    diagnosticoMoneda: Moneda;
    diagnosticoCuentaId: number | null;
  };
  cuentas: ReadonlyArray<CajaDelCobro>;
  cotizacion: number;
}): JSX.Element {
  const importe = form.diagnosticoImporte ?? 0;
  if (importe <= 0) return <>Sin importe no se cobra nada y el equipo entra igual.</>;

  const cuenta = cuentas.find((c) => c.id === form.diagnosticoCuentaId);
  if (!cuenta) return <>Elegí la caja para ver cuánto entra.</>;
  if (cuenta.moneda === form.diagnosticoMoneda) {
    return (
      <>
        Entran <Importe monto={importe} moneda={cuenta.moneda} tamano="sm" /> a {cuenta.nombre}.
      </>
    );
  }
  if (cotizacion <= 0) {
    return (
      <>
        Entra a {cuenta.nombre}, convertido a {cuenta.moneda}.
      </>
    );
  }
  const convertido = convertir(importe, form.diagnosticoMoneda, cuenta.moneda, cotizacion);
  return (
    <>
      Entran <Importe monto={convertido} moneda={cuenta.moneda} tamano="sm" /> a {cuenta.nombre}.
    </>
  );
}
